// ────────────────────────────────────────────────────────────────────────────
// Preview data for the builder: the operator's real organisation printed
// against synthetic customer / line data.
//
// Seeing your own name, address, VAT number, bank details and logo in the
// preview is the difference between "designing a template" and "designing our
// invoice". The customer and lines stay synthetic because the whole point of
// the preview is to be reproducible — same layout, same page, every time.
//
// The logo comes from `useOrganisationLogoDataUri`, NOT from the org's
// `logoUrl`. `logoUrl` is a storage SAS URL: fine for an `img` tag, useless
// here. react-pdf loads `<Image src>` by fetching it, and a cross-origin fetch
// to the storage account is refused unless the account carries a CORS rule for
// the app origin — so the bytes come back through our own API instead.
// ────────────────────────────────────────────────────────────────────────────
import {
  useAddress,
  useOrganisation,
  useOrganisationBankAccounts,
  useOrganisationLogoDataUri,
} from '@my/directory-api/hooks';
import { useMemo } from 'react';
import { selectBankAccount, type BankAccountStatus } from '@/utils/bankAccounts';
import { buildSampleData } from './sampleData';
import { splitAddress, type DocumentData } from './documentData';
import type { DocumentType } from './types';

/**
 * Why the preview has no logo to draw. Surfaced in the Header inspector —
 * "Show logo" doing nothing with no explanation is the worst outcome here.
 */
export type LogoStatus =
  /** The organisation has no logo uploaded. */
  | 'none'
  /** Still fetching. */
  | 'loading'
  /** Loaded and embedded in the preview. */
  | 'ready'
  /** The organisation has one, but it could not be loaded. */
  | 'failed';

/** Which bank account the Payment details band will print, and why. */
export interface BankPreviewStatus {
  status: BankAccountStatus;
  /** The document currency the account was matched on. */
  currencyIsoCode: string;
  /** Name of the account that will print, when one resolved. */
  accountName: string | null;
  /** How many accounts the organisation holds in that currency. */
  candidates: number;
}

export interface OrgPreviewData {
  data: DocumentData;
  logoStatus: LogoStatus;
  /** Server-side reason when `logoStatus` is `failed`, for the inspector note. */
  logoError: string | null;
  bank: BankPreviewStatus;
}

export function useOrgPreviewData(
  organisationId: string | null,
  docType: DocumentType,
): OrgPreviewData {
  const orgQuery = useOrganisation(organisationId);
  const org = orgQuery.data;
  const addressQuery = useAddress(org?.mainOfficeAddressId ?? null);
  const banksQuery = useOrganisationBankAccounts(organisationId);

  // `logo` is the blob name — its presence is the cheap "does one exist?" test,
  // so a logo-less org never fires a request that is bound to 404.
  const hasLogo = Boolean(org?.logo);
  const logoQuery = useOrganisationLogoDataUri(organisationId, hasLogo);

  const logoStatus: LogoStatus = !hasLogo
    ? 'none'
    : logoQuery.isPending
      ? 'loading'
      : logoQuery.data
        ? 'ready'
        : 'failed';

  const logoError = logoStatus === 'failed' ? (logoQuery.error?.message ?? null) : null;

  const data = useMemo(() => {
    const base = buildSampleData(docType);
    if (!org) return base;

    // Addresses are stored as a single Google-formatted `fullAddress`, so the
    // printed block is that string split on its commas.
    const address = addressQuery.data;
    const addressLines = address?.fullAddress
      ? splitAddress(address.fullAddress)
      : base.organisation.addressLines;

    // Strictly by currency, with NO fallback to another account: printing a
    // EUR IBAN on a GBP invoice would send the customer's payment to the wrong
    // place. No match means no payment block, and the inspector says so.
    const currency = org.currencyIsoCode || base.doc.currencyIsoCode;
    const account = selectBankAccount(banksQuery.data, currency).account;

    return {
      ...base,
      organisation: {
        name: org.invoicingName || org.name,
        addressLines,
        taxNumber: org.taxNumber,
        registrationNumber: org.registrationNumber,
        phone: null,
        email: null,
        website: null,
        logoUrl: logoQuery.data ?? null,
      },
      doc: { ...base.doc, currencyIsoCode: currency },
      bank: account
        ? {
            accountName: account.name,
            bankName: account.bankName,
            iban: account.iban,
            bic: account.bic,
            sortCode: account.sortCode,
            accountNumber: account.accountNumber,
          }
        : undefined,
    };
  }, [docType, org, addressQuery.data, banksQuery.data, logoQuery.data]);

  // Reported separately from `data` so the inspector can explain an empty block
  // instead of the operator wondering why the band prints nothing.
  const bankCurrency = org?.currencyIsoCode || data.doc.currencyIsoCode;
  const selection = selectBankAccount(banksQuery.data, bankCurrency);
  const bank: BankPreviewStatus = {
    status: selection.status,
    currencyIsoCode: bankCurrency,
    accountName: selection.account?.name ?? null,
    candidates: selection.candidates,
  };

  return { data, logoStatus, logoError, bank };
}
